import { Tabs } from '@mantine/core';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import type React from 'react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { employeeProjectModelService } from '../services/employeeProjectModelService';
import { colors } from '../theme/colors';
import './Tabs.css';

function handleTabChange(
  route: string | null,
  navigate: ReturnType<typeof useNavigate>,
  isAdmin: boolean,
) {
  if (!route) return;
  if (route === 'admin-settings') {
    if (!isAdmin) {
      navigate({ to: '/time-entry' });
      return;
    }
    navigate({ to: '/admin-settings/logged-hours' });
    return;
  }
  navigate({ to: `/${route}` });
}

interface TabsComponentProps {
  children?: React.ReactNode;
}

export const TabsComponent: React.FC<TabsComponentProps> = ({ children }) => {
  const navigate = useNavigate();
  const router = useRouterState();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const isAdmin = userRole === 'admin';
  const isProjectManager = userRole === 'project manager' || userRole === 'project_manager';

  // Employee project model administration moved to another application; this
  // request is used purely as a project-role access check.
  const {
    error: projectModelsError,
    isSuccess: hasProjectRoleAccess,
  } = useQuery({
    queryKey: ['employee-project-models', 'access-check'],
    queryFn: () => employeeProjectModelService.getEmployeeProjectModels(),
    enabled: !isAdmin && !!user,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const showCustomerReport =
    isAdmin || isProjectManager || (hasProjectRoleAccess && !projectModelsError);

  const currentPath = router.location.pathname;
  const activeTab = useMemo(() => {
    if (currentPath.startsWith('/overview')) return 'overview';
    if (currentPath.startsWith('/time-entry')) return 'time-entry';
    if (isAdmin && currentPath.startsWith('/admin-settings')) return 'admin-settings';
    if (showCustomerReport && currentPath.startsWith('/customer-report')) {
      return 'customer-report';
    }
    return 'time-entry';
  }, [currentPath, isAdmin, showCustomerReport]);

  return (
    <Tabs
      value={activeTab}
      onChange={(value) => handleTabChange(value, navigate, isAdmin)}
      className="root-nav-tabs"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Tabs.List
        style={{
          borderBottom: `2px solid ${colors.neutral.grey[200]}`,
          gap: '0.5rem',
          paddingTop: '1rem',
          paddingBottom: '0.5rem',
          flexShrink: 0,
          backgroundColor: colors.neutral.grey[50],
        }}
      >
        <Tabs.Tab value="overview">Overview</Tabs.Tab>
        <Tabs.Tab value="time-entry">Time Entry</Tabs.Tab>
        {showCustomerReport && <Tabs.Tab value="customer-report">Customer Report</Tabs.Tab>}
        {isAdmin && <Tabs.Tab value="admin-settings">Admin Settings</Tabs.Tab>}
      </Tabs.List>

      <Tabs.Panel
        value={activeTab}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Tabs.Panel>
    </Tabs>
  );
};
